import { Input, type InputProps } from "@heroui/react";
import { ButtonOverride as Button } from "@/components/Buttons/ButtonOverride";
import Col from "@/components/Layout/Flex/Col";
import Centered from "../Layout/Flex/Centered";
import type { TokenConfigType } from "@/constants/types/currency";
import { fallbackChainId, type ChainType } from "@/constants/chain-specific";
import { forwardRef, useState } from "react";
import type { Command } from "cmdk";
import { type Address, getAddress, isAddress } from "viem";
import { useSearchToken } from "@/hooks/useSearchToken";
import { importedTokensAtom } from "@/config/atoms/tokens/customTokens";
import { useAtom } from "jotai/react";
import type { ImportedTokens } from "@/config/atoms/tokens/customTokens";

interface Props extends InputProps {
  showBalance?: boolean;
  onTokenChange?: (token: TokenConfigType) => void;

  chain?: ChainType;
}

const TokenInput = forwardRef<React.ElementRef<typeof Command.Input>, Props>(
  ({ className, showBalance, onTokenChange, chain, ...props }, ref) => {
    const [inputValue, setInputValue] = useState<string>("");
    const [isValidAddress, setIsValidAddress] = useState<boolean>(false);
    const [tokenAddress, setTokenAddress] = useState<string | undefined>(
      undefined
    );
    const { token } = useSearchToken(
      isAddress(tokenAddress as Address) ? getAddress(tokenAddress as Address) :
      ("0x0000000000000000000000000000000000000000" as Address), chain ?? fallbackChainId
    );

    const [importedTokens, setImportedTokens] = useAtom(importedTokensAtom);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setInputValue(value);
      let tokenAddress = value
      try {
        tokenAddress = getAddress(value)
      } catch {
        console.log("Invalid address")
      }
      if (isAddress(tokenAddress)) {
        setIsValidAddress(true);
        setTokenAddress(tokenAddress);
      } else {
        setIsValidAddress(false);
        setTokenAddress(undefined);
      }
    };

    return (
      <Centered className="w-full">
        <Col className="items-center w-full">
          <Input
            ref={ref}
            isClearable
            variant="faded"
            classNames={{
              label: "text-xs md:text-sm",
            }}
            labelPlacement="outside"
            onChange={handleChange}
            onClear={() =>
              handleChange({
                target: { value: "" },
              } as React.ChangeEvent<HTMLInputElement>)
            }
            {...props}
          />
          {isValidAddress && token && (
            <div className="mt-4 mb-4">
              <div className="inline-flex">
                <img src={token.logo ? token.logo : `https://chewyswap.dog/images/${Number(chain ?? 109)}/tokens/${getAddress(tokenAddress as Address)}.webp`} width={20} height={20} alt="Token Logo" />
                <span className="pl-2">Name: {token.name}</span>
                </div>
              <div>Symbol: {token.symbol}</div>
              <div>Balance: {token.balance.number}</div>
          <Button className="mt-4" color="secondary" size="md" onPress={() => {
            setImportedTokens((prev: ImportedTokens) => {
              const chainId = chain ?? fallbackChainId;
              const chainTokens = prev[chainId] || [];
              if (!chainTokens.find((t: TokenConfigType) => t.address === tokenAddress)) {
                const newToken: TokenConfigType = {
                  address: tokenAddress as Address,
                  name: token.name,
                  symbol: token.symbol,
                  decimals: token.decimals,
                  logo: token.logo ? token.logo : `https://chewyswap.dog/images/${Number(chainId)}/tokens/${getAddress(tokenAddress as Address)}.webp`,
                };
                return {
                  ...prev,
                  [chainId]: [...chainTokens, newToken]
                };
              }
              return prev;
            });
            onTokenChange?.({
              address: tokenAddress as Address,
              name: token.name,
              symbol: token.symbol,
              decimals: token.decimals,
              logo: token.logo ? token.logo : `https://chewyswap.dog/images/${Number(chain ?? 109)}/tokens/${getAddress(tokenAddress as Address)}.webp`,
            });
          }}>Import{token.name ? ` ${token.name}` : ''}</Button>
            </div>
          )}
        </Col>
      </Centered>
    );
  }
);

TokenInput.displayName = "TokenInput";

export default TokenInput;
